import api from '../../../services/api';

const BASE_PATH = '/workflows';
const WORKFLOW_EXECUTE_PATH = '/workflow';

class WorkflowAPIService {
  constructor() {
    this.api = api;
    this.basePath = BASE_PATH;
    this.executePath = WORKFLOW_EXECUTE_PATH;
  }

  /**
   * Lấy danh sách workflows
   */
  async getWorkflows(params = {}) {
    try {
      const response = await this.api.get(this.basePath, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Lấy workflow theo ID
   */
  async getWorkflow(workflowId) {
    try {
      const response = await this.api.get(`${this.basePath}/${workflowId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Tạo workflow mới
   */
  async createWorkflow(workflowData) {
    try {
      const response = await this.api.post(this.basePath, workflowData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Cập nhật workflow
   */
  async updateWorkflow(workflowId, updateData) {
    try {
      const response = await this.api.put(`${this.basePath}/${workflowId}`, updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Xóa workflow
   */
  async deleteWorkflow(id) {
    try {
      const response = await this.api.delete(`${this.basePath}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting workflow:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Tạo workflow từ template
   */
  async createFromTemplate(templateId, newData) {
    try {
      const response = await this.api.post(`${this.basePath}/templates/${templateId}`, newData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Duplicate workflow
   */
  async duplicateWorkflow(workflowId, newName) {
    try {
      const response = await this.api.post(`${this.basePath}/${workflowId}/duplicate`, { name: newName });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update execution statistics
   */
  async updateExecutionStats(workflowId) {
    try {
      const response = await this.api.post(`${this.basePath}/${workflowId}/execute`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Save complete workflow (create or update based on existence)
   */
  async saveWorkflow(workflowData) {
    try {
      if (workflowData.id) {
        // Update existing workflow
        return await this.updateWorkflow(workflowData.id, workflowData);
      } else {
        // Create new workflow
        return await this.createWorkflow(workflowData);
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    const errorDetails = error.response?.data?.errors || [];
    
    return {
      message: errorMessage,
      details: errorDetails,
      status: error.response?.status || 500,
      success: false
    };
  }

  /**
   * Execute workflow
   */
  async executeWorkflow(id, data = {}) {
    try {
      // Construct the payload expected by the backend
      const payload = {
          workflow: {
              nodes: data.nodes || [],
              edges: data.edges || []
          },
          context: {
              variables: data.variables || {},
              nodeOverrides: data.nodeOverrides || {},
              executionId: data.executionId || null, // Pass executionId for debug continuation
              previousResults: data.previousResults || null // Pass previous results to skip already executed nodes
          }
      };

      // Use the new endpoint /api/workflow/execute
      const response = await this.api.post(`${this.executePath}/execute`, payload);
      return response.data;
    } catch (error) {
      console.error('Error executing workflow:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Execute workflow asynchronously - returns executionId immediately
   * Use this with SSE stream for real-time progress
   */
  async executeWorkflowAsync(id, data = {}) {
    try {
      // Construct the payload expected by the backend
      const payload = {
          workflow: {
              nodes: data.nodes || [],
              edges: data.edges || []
          },
          context: {
              variables: data.variables || {},
              nodeOverrides: data.nodeOverrides || {},
              executionId: data.executionId || null,
              previousResults: data.previousResults || null
          }
      };

      // Use async=true query param to get executionId immediately
      const response = await this.api.post(`${this.executePath}/execute?async=true`, payload);
      return response.data;
    } catch (error) {
      console.error('Error starting async workflow:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Get execution result after workflow completes
   * This fetches the full result data (SSE only sends preview)
   */
  async getExecutionResult(executionId) {
    try {
      const response = await this.api.get(`${this.executePath}/result/${executionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching execution result:', error);
      // Return a basic success response if result endpoint doesn't exist yet
      // The SSE events already provided the status
      return { success: true, message: 'Execution completed' };
    }
  }

  /**
   * Get SSE stream URL for workflow execution
   */
  getSSEStreamUrl(executionId) {
    const baseURL = this.api.defaults.baseURL || '';
    return `${baseURL}${this.executePath}/stream/${executionId}`;
  }

  /**
   * Get download URL for a file
   * This uses the backend API to securely serve files
   * @param {string} filePath - Internal file path (not exposed to user)
   * @returns {string} Download URL
   */
  getDownloadUrl(filePath) {
    // Encode the file path to be safe in URL
    const encodedPath = encodeURIComponent(filePath);
    const token = localStorage.getItem('token');
    const baseURL = this.api.defaults.baseURL || '';
    
    // Return the download API URL with auth token as query param for direct download
    return `${baseURL}${this.executePath}/download?filePath=${encodedPath}&token=${token}`;
  }

  /**
   * Download file via API
   * @param {string} filePath - Internal file path
   * @param {string} fileName - Suggested file name for download
   */
  async downloadFile(filePath, fileName = 'download.xlsx') {
    try {
      const response = await this.api.get(`${this.executePath}/download`, {
        params: { filePath },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error downloading file:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Preview CSV file - get sample rows
   * @param {string} filePath - Path to CSV file
   * @param {number} limit - Number of rows to preview (default 200)
   * @returns {Object} { success, data: { headers, rows, totalRows } }
   */
  async previewCsv(filePath, limit = 200) {
    try {
      const response = await this.api.get(`${this.executePath}/preview-csv`, {
        params: { filePath, limit }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error previewing CSV:', error);
      return { success: false, message: error.message };
    }
  }
}

// Export singleton instance
export default new WorkflowAPIService();